import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithCustomToken,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth';
import { firebaseAuth, googleProvider } from './firebase';

type AuthState = {
  user: User | null;
  loading: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithCustom: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(firebaseAuth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const value: AuthState = {
    user,
    loading,
    signInWithEmail: async (email, password) => {
      await signInWithEmailAndPassword(firebaseAuth, email, password);
    },
    signUpWithEmail: async (email, password) => {
      await createUserWithEmailAndPassword(firebaseAuth, email, password);
    },
    signInWithGoogle: async () => {
      await signInWithPopup(firebaseAuth, googleProvider);
    },
    signInWithCustom: async (token) => {
      // Used by passwordless flows (email-code, passkey) — backend mints
      // a Firebase custom token after verifying the alternative factor.
      await signInWithCustomToken(firebaseAuth, token);
    },
    signOut: async () => {
      await fbSignOut(firebaseAuth);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * Returns a Firebase ID token (JWT) for the current user, refreshing if needed.
 * Returns null if not signed in.
 */
export async function getIdToken(): Promise<string | null> {
  const u = firebaseAuth.currentUser;
  if (!u) return null;
  return u.getIdToken();
}
